import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSportsLang } from '../contexts/SportsLangContext';
import { VUKZ } from '../theme/vukzTheme';


const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading } = useAuth();

  // INV-2c: round-trip do convite — redirect sanitizado (fail-closed: apenas
  // rota interna; nunca '//' nem URL absoluta) + invite_token como query
  // própria (nunca extraído de dentro do redirect).
  const rawRedirect = searchParams.get('redirect');
  const safeRedirect =
    rawRedirect?.startsWith('/') && !rawRedirect.startsWith('//')
      ? rawRedirect
      : '/central';
  const inviteToken = searchParams.get('invite_token');

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});

  const { lang } = useSportsLang();
  const T = {
    en: {
      heading: 'Sign in to your account',
      noClub: "Don't have a club yet?",
      registerLink: 'Register your club',
      emailLabel: 'Email',
      emailPlaceholder: 'your@email.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Your password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      loading: 'Signing in...',
      submit: 'Sign in',
      lgpd: '🔒 Your data is protected under LGPD',
      errEmailRequired: 'Email is required',
      errEmailInvalid: 'Invalid email',
      errPasswordRequired: 'Password is required',
      errPasswordLength: 'Password must be at least 6 characters',
      errGeneral: 'Login error. Please try again.',
    },
    pt: {
      heading: 'Entre na sua conta',
      noClub: 'Ainda não tem um clube?',
      registerLink: 'Cadastre seu clube',
      emailLabel: 'Email',
      emailPlaceholder: 'seu@email.com',
      passwordLabel: 'Senha',
      passwordPlaceholder: 'Sua senha',
      rememberMe: 'Lembrar de mim',
      forgotPassword: 'Esqueceu a senha?',
      loading: 'Entrando...',
      submit: 'Entrar',
      lgpd: '🔒 Seus dados estão protegidos de acordo com a LGPD',
      errEmailRequired: 'Email é obrigatório',
      errEmailInvalid: 'Email inválido',
      errPasswordRequired: 'Senha é obrigatória',
      errPasswordLength: 'Senha deve ter pelo menos 6 caracteres',
      errGeneral: 'Erro ao fazer login. Tente novamente.',
    },
  }[lang] || {};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpa erro do campo quando usuário começa a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = T.errEmailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = T.errEmailInvalid;
    }

    if (!formData.password) {
      newErrors.password = T.errPasswordRequired;
    } else if (formData.password.length < 6) {
      newErrors.password = T.errPasswordLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const result = await login(formData.email, formData.password, inviteToken || null);
    
    if (result.success) {
      navigate(safeRedirect);
    } else {
      // OPÇÃO A: Redirecionar para /pending-approval se ACCOUNT_AWAITING_LAUNCH
      if (result.code === 'ACCOUNT_AWAITING_LAUNCH') {
        navigate('/pending-approval');
        return;
      }
      
      setErrors({
        general: result.error || T.errGeneral
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-12 w-auto" />
        </div>
        
        <h2 className="mt-6 text-center text-3xl font-extrabold" style={{ color: VUKZ.text }}>
          {T.heading}
        </h2>
        <p className="mt-2 text-center text-sm" style={{ color: VUKZ.textLight }}>
          {T.noClub}{' '}
          <Link 
            to="/register" 
            className="font-semibold underline"
            style={{ color: VUKZ.text }}
          >
            {T.registerLink}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Erro geral */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-sm text-red-600">
                  {errors.general}
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {T.emailLabel}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md 
                           placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-opacity-50 
                           sm:text-sm ${errors.email 
                             ? 'border-red-300 focus:border-red-300 focus:ring-red-500' 
                             : 'border-gray-300 focus:border-transparent'
                           }`}
                  style={{ 
                    '--tw-ring-color': !errors.email ? VUKZ.primary + '80' : '#ef4444'
                  }}
                  placeholder={T.emailPlaceholder}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Senha */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {T.passwordLabel}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-3 py-2 border rounded-md 
                           placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-opacity-50 
                           sm:text-sm ${errors.password 
                             ? 'border-red-300 focus:border-red-300 focus:ring-red-500' 
                             : 'border-gray-300 focus:border-transparent'
                           }`}
                  style={{ 
                    '--tw-ring-color': !errors.password ? VUKZ.primary + '80' : '#ef4444'
                  }}
                  placeholder={T.passwordPlaceholder}
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>

            {/* Lembrar de mim e esqueci senha */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  {T.rememberMe}
                </label>
              </div>

              <div className="text-sm">
                <Link 
                  to="/forgot-password" 
                  className="font-medium hover:underline"
                  style={{ color: VUKZ.primary }}
                >
                  {T.forgotPassword}
                </Link>
              </div>
            </div>

            {/* Botão de submit */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent 
                         text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 
                         disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                style={{ backgroundColor: VUKZ.primary }}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {T.loading}
                  </div>
                ) : (
                  T.submit
                )}
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs" style={{ color: VUKZ.textLight }}>
              {T.lgpd}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;